// src/pages/SuperAdmin/EventsAndBannersManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  UncontrolledTooltip,
} from 'reactstrap';
import { get, post, put, del } from 'helpers/api_helper';
import { showSuccessAlert, showErrorAlert } from 'pages/utils/alertMessages';
import {
  RiMegaphoneLine,
  RiCalendarEventLine,
  RiNotification3Line,
  RiMapPin2Line,
  RiGlobalLine,
  RiEyeLine,
  RiCursorLine,
  RiAddLine,
  RiEditLine,
  RiDeleteBinLine,
  RiFileCopyLine,
  RiRefreshLine,
  RiCheckDoubleLine,
  RiMagicLine,
  RiCloseLine,
  RiExternalLinkLine,
  RiTimerLine,
  RiShieldCheckLine,
} from 'react-icons/ri';

const BANNER_STYLES = [
  { id: 'primary', label: 'Classic Cobalt Blue', bg: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', text: '#ffffff', border: '#60a5fa' },
  { id: 'gradient-purple', label: 'Royal Violet & Indigo', bg: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 50%, #c026d3 100%)', text: '#ffffff', border: '#a855f7' },
  { id: 'gradient-amber', label: 'Golden Amber & Orange', bg: 'linear-gradient(135deg, #78350f 0%, #d97706 50%, #f59e0b 100%)', text: '#ffffff', border: '#fbbf24' },
  { id: 'gradient-emerald', label: 'Emerald Jade & Mint', bg: 'linear-gradient(135deg, #064e3b 0%, #059669 50%, #10b981 100%)', text: '#ffffff', border: '#34d399' },
  { id: 'gradient-rose', label: 'Crimson Rose & Coral', bg: 'linear-gradient(135deg, #881337 0%, #e11d48 50%, #f43f5e 100%)', text: '#ffffff', border: '#fb7185' },
  { id: 'danger', label: 'Ruby Emergency Red', bg: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)', text: '#ffffff', border: '#f87171' },
  { id: 'info', label: 'Deep Ocean Cyan', bg: 'linear-gradient(135deg, #0f172a 0%, #0284c7 100%)', text: '#ffffff', border: '#38bdf8' },
  { id: 'warning', label: 'Dark Charcoal Alert', bg: 'linear-gradient(135deg, #18181b 0%, #27272a 100%)', text: '#facc15', border: '#eab308' },
];

const PRESET_INDIAN_STATES = [
  'Gujarat',
  'Maharashtra',
  'Karnataka',
  'Delhi',
  'Tamil Nadu',
  'Rajasthan',
  'Uttar Pradesh',
  'Punjab',
  'West Bengal',
  'Telangana',
  'Kerala',
  'Madhya Pradesh',
];

const INITIAL_FORM = {
  title: '',
  message: '',
  type: 'announcement',
  priority: 'medium',
  banner_style: 'primary',
  badge_text: 'ANNOUNCEMENT',
  icon: 'bx bx-bell',
  start_date: '',
  end_date: '',
  status: 1,
  is_dismissible: 1,
  action_text: '',
  action_url: '',
  open_in_new_tab: 0,
  target_scope: 'all',
  target_country: 'India',
  target_state: 'All',
  target_city: 'All',
  target_pincode: '',
};

const EventsAndBannersManager = () => {
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Test Targeting Simulator Modal
  const [simModalOpen, setSimModalOpen] = useState(false);
  const [simState, setSimState] = useState('Gujarat');
  const [simCity, setSimCity] = useState('Ahmedabad');
  const [simPincode, setSimPincode] = useState('380015');

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await get('/banners');
      if (res?.success || res?.data) {
        setBanners(res.data || []);
        setStats(res.stats || null);
      }
    } catch (err) {
      console.error('Failed to load platform banners:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 16);
    const nextMonth = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 16);

    setFormData({
      ...INITIAL_FORM,
      start_date: todayStr,
      end_date: nextMonth,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (banner) => {
    setEditingId(banner.id);
    setFormData({
      title: banner.title || '',
      message: banner.message || '',
      type: banner.type || 'announcement',
      priority: banner.priority || 'medium',
      banner_style: banner.banner_style || 'primary',
      badge_text: banner.badge_text || '',
      icon: banner.icon || 'bx bx-bell',
      start_date: banner.start_date ? new Date(banner.start_date).toISOString().slice(0, 16) : '',
      end_date: banner.end_date ? new Date(banner.end_date).toISOString().slice(0, 16) : '',
      status: banner.status ?? 1,
      is_dismissible: banner.is_dismissible ?? 1,
      action_text: banner.action_text || '',
      action_url: banner.action_url || '',
      open_in_new_tab: banner.open_in_new_tab ?? 0,
      target_scope: banner.target_scope || 'all',
      target_country: banner.target_country || 'India',
      target_state: banner.target_state || 'All',
      target_city: banner.target_city || 'All',
      target_pincode: banner.target_pincode || '',
    });
    setModalOpen(true);
  };

  const handleDuplicate = (banner) => {
    setEditingId(null);
    setFormData({
      ...banner,
      title: `(Copy) ${banner.title}`,
      start_date: new Date().toISOString().slice(0, 16),
      end_date: banner.end_date ? new Date(banner.end_date).toISOString().slice(0, 16) : '',
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!formData.title.trim()) {
      showErrorAlert('Please enter banner title');
      return;
    }
    if (!formData.message.trim()) {
      showErrorAlert('Please enter message text');
      return;
    }

    setSubmitting(true);
    try {
      let res;
      if (editingId) {
        res = await put(`/banners/${editingId}`, formData);
      } else {
        res = await post('/banners', formData);
      }

      if (res?.success) {
        showSuccessAlert(editingId ? 'Banner updated successfully!' : 'Banner created & scheduled successfully!');
        setModalOpen(false);
        fetchBanners();
      } else {
        showErrorAlert(res?.message || 'Failed to save banner');
      }
    } catch (err) {
      showErrorAlert(err?.message || 'Error occurred while saving banner');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const res = await (await import('helpers/api_helper')).patch ? await (await import('helpers/api_helper')).patch(`/banners/${id}/toggle-status`, {}) : await post(`/banners/${id}/click`, {});
      // Fallback put toggle
      await put(`/banners/${id}`, { status: currentStatus === 1 ? 0 : 1 });
      showSuccessAlert(`Banner #${id} status changed to ${currentStatus === 1 ? 'Inactive' : 'Active'}`);
      fetchBanners();
    } catch (err) {
      showErrorAlert('Failed to toggle status');
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete banner "${title}"?`)) return;
    try {
      const res = await del(`/banners/${id}`);
      if (res?.success) {
        showSuccessAlert('Banner deleted successfully');
        fetchBanners();
      } else {
        showErrorAlert(res?.message || 'Failed to delete');
      }
    } catch (err) {
      showErrorAlert('Failed to delete banner');
    }
  };

  const handleSeedSamples = async () => {
    if (!window.confirm('Seed standard dental events (Gujarat Dental Summit, AI Radiology Masterclass, Maintenance Alert)?')) return;
    try {
      const res = await post('/banners/seed-sample', {});
      if (res?.success) {
        showSuccessAlert('Sample standard events & announcements populated!');
        fetchBanners();
      }
    } catch (err) {
      showErrorAlert('Failed to seed sample banners');
    }
  };

  // State selection helper
  const handleToggleStatePreset = (stateName) => {
    let current = (formData.target_state || '').trim();
    if (current.toLowerCase() === 'all' || current === '') {
      setFormData({ ...formData, target_state: stateName, target_scope: 'geo' });
      return;
    }
    const list = current.split(',').map((s) => s.trim()).filter(Boolean);
    if (list.includes(stateName)) {
      const updated = list.filter((s) => s !== stateName);
      setFormData({ ...formData, target_state: updated.length ? updated.join(', ') : 'All' });
    } else {
      list.push(stateName);
      setFormData({ ...formData, target_state: list.join(', '), target_scope: 'geo' });
    }
  };

  // Filtered banners
  const filteredBanners = useMemo(() => {
    return banners.filter((b) => {
      // Search
      if (search) {
        const query = search.toLowerCase();
        const matchTitle = (b.title || '').toLowerCase().includes(query);
        const matchMsg = (b.message || '').toLowerCase().includes(query);
        const matchState = (b.target_state || '').toLowerCase().includes(query);
        const matchCity = (b.target_city || '').toLowerCase().includes(query);
        if (!matchTitle && !matchMsg && !matchState && !matchCity) return false;
      }

      // Status
      const now = new Date();
      if (statusFilter === 'active') {
        const isExp = b.end_date && new Date(b.end_date) < now;
        const isSched = b.start_date && new Date(b.start_date) > now;
        if (b.status !== 1 || isExp || isSched) return false;
      } else if (statusFilter === 'scheduled') {
        const isSched = b.start_date && new Date(b.start_date) > now;
        if (b.status !== 1 || !isSched) return false;
      } else if (statusFilter === 'expired') {
        const isExp = b.end_date && new Date(b.end_date) < now;
        if (b.status === 0 || isExp) {
          // match expired
        } else {
          return false;
        }
      } else if (statusFilter === 'inactive') {
        if (b.status !== 0) return false;
      }

      // Type
      if (typeFilter !== 'all' && b.type !== typeFilter) return false;

      // Scope
      if (scopeFilter === 'all' && b.target_scope !== 'all') return false;
      if (scopeFilter === 'geo' && b.target_scope !== 'geo' && b.target_scope !== 'custom') return false;

      return true;
    });
  }, [banners, search, statusFilter, typeFilter, scopeFilter]);

  // Current Style config for live preview
  const currentStyleObj = BANNER_STYLES.find((s) => s.id === formData.banner_style) || BANNER_STYLES[0];

  return (
    <div className="events-banners-manager">
      {/* Top Stat Cards */}
      <Row className="mb-3">
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-xs rounded-3 mb-2">
            <CardBody className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <p className="text-muted text-uppercase font-size-11 font-weight-bold mb-1">Total Announcements</p>
                <h4 className="mb-0 font-weight-bold text-dark">{stats?.total ?? banners.length}</h4>
                <small className="text-success font-size-11">
                  <i className="bx bx-check-circle" /> {stats?.active ?? 0} Live Now
                </small>
              </div>
              <div className="avatar-sm rounded-circle bg-primary bg-soft d-flex align-items-center justify-content-center text-primary font-size-22">
                <RiMegaphoneLine />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md={3} sm={6}>
          <Card className="border-0 shadow-xs rounded-3 mb-2">
            <CardBody className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <p className="text-muted text-uppercase font-size-11 font-weight-bold mb-1">Targeted / Localized</p>
                <h4 className="mb-0 font-weight-bold text-primary">{stats?.targeted ?? 0}</h4>
                <small className="text-muted font-size-11">
                  <i className="bx bx-map-pin" /> State & City Specific
                </small>
              </div>
              <div className="avatar-sm rounded-circle bg-info bg-soft d-flex align-items-center justify-content-center text-info font-size-22">
                <RiMapPin2Line />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md={3} sm={6}>
          <Card className="border-0 shadow-xs rounded-3 mb-2">
            <CardBody className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <p className="text-muted text-uppercase font-size-11 font-weight-bold mb-1">Total Impressions</p>
                <h4 className="mb-0 font-weight-bold text-dark">{Number(stats?.totalViews ?? 0).toLocaleString()}</h4>
                <small className="text-muted font-size-11">
                  <i className="bx bx-show" /> Tenant Topbar Views
                </small>
              </div>
              <div className="avatar-sm rounded-circle bg-warning bg-soft d-flex align-items-center justify-content-center text-warning font-size-22">
                <RiEyeLine />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md={3} sm={6}>
          <Card className="border-0 shadow-xs rounded-3 mb-2">
            <CardBody className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <p className="text-muted text-uppercase font-size-11 font-weight-bold mb-1">Action Clicks & CTR</p>
                <h4 className="mb-0 font-weight-bold text-success">
                  {Number(stats?.totalClicks ?? 0).toLocaleString()}{' '}
                  <span className="font-size-12 text-muted font-weight-normal">({stats?.ctr ?? '0%'})</span>
                </h4>
                <small className="text-success font-size-11">
                  <i className="bx bx-pointer" /> Button Engagements
                </small>
              </div>
              <div className="avatar-sm rounded-circle bg-success bg-soft d-flex align-items-center justify-content-center text-success font-size-22">
                <RiCursorLine />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Control Header */}
      <Card className="border-0 shadow-xs rounded-3 mb-3">
        <CardBody className="p-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div className="position-relative" style={{ minWidth: 260 }}>
                <Input
                  type="text"
                  className="form-control form-control-sm pe-4 rounded-pill"
                  placeholder="Search title, state, city..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <i className="bx bx-search position-absolute text-muted" style={{ right: 12, top: 8 }} />
              </div>

              {/* Status Filter */}
              <div className="btn-group btn-group-sm">
                <Button
                  color={statusFilter === 'all' ? 'primary' : 'light'}
                  onClick={() => setStatusFilter('all')}
                  className="px-2.5 font-size-12"
                >
                  All ({banners.length})
                </Button>
                <Button
                  color={statusFilter === 'active' ? 'primary' : 'light'}
                  onClick={() => setStatusFilter('active')}
                  className="px-2.5 font-size-12"
                >
                  Active ({stats?.active ?? 0})
                </Button>
                <Button
                  color={statusFilter === 'scheduled' ? 'primary' : 'light'}
                  onClick={() => setStatusFilter('scheduled')}
                  className="px-2.5 font-size-12"
                >
                  Scheduled ({stats?.scheduled ?? 0})
                </Button>
                <Button
                  color={statusFilter === 'expired' ? 'primary' : 'light'}
                  onClick={() => setStatusFilter('expired')}
                  className="px-2.5 font-size-12"
                >
                  Expired ({stats?.expired ?? 0})
                </Button>
              </div>

              {/* Type Filter */}
              <Input
                type="select"
                className="form-select form-select-sm rounded-pill font-size-12"
                style={{ width: 140 }}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="event">🎪 Events</option>
                <option value="announcement">📢 Announcements</option>
                <option value="notification">🔔 Notifications</option>
                <option value="promotional">🎁 Promotional</option>
                <option value="maintenance">🔧 Maintenance</option>
              </Input>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                color="outline-secondary"
                size="sm"
                className="rounded-pill font-size-12 d-inline-flex align-items-center gap-1"
                onClick={() => setSimModalOpen(true)}
                title="Test how targeting appears for Gujarat vs Maharashtra clinics"
              >
                <RiMapPin2Line />
                <span>Test Targeting</span>
              </Button>

              <Button
                color="outline-info"
                size="sm"
                className="rounded-pill font-size-12 d-inline-flex align-items-center gap-1"
                onClick={handleSeedSamples}
              >
                <RiMagicLine />
                <span>Seed Sample Events</span>
              </Button>

              <Button
                color="primary"
                size="sm"
                className="rounded-pill font-size-12 d-inline-flex align-items-center gap-1 shadow-2xs font-weight-semibold"
                onClick={handleOpenCreate}
              >
                <RiAddLine className="font-size-14" />
                <span>Create Event / Banner</span>
              </Button>

              <Button
                color="light"
                size="sm"
                className="rounded-circle p-1"
                onClick={fetchBanners}
                title="Refresh"
              >
                <RiRefreshLine className={`font-size-14 ${loading ? 'bx-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Banners List */}
      <Card className="border-0 shadow-xs rounded-3">
        <CardBody className="p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 font-size-13">
              <thead className="table-light text-muted font-size-11 text-uppercase">
                <tr>
                  <th style={{ width: 50 }}>Status</th>
                  <th>Type & Badge</th>
                  <th style={{ minWidth: 260 }}>Title & Message</th>
                  <th>Target Audience</th>
                  <th>Schedule / Validity</th>
                  <th>Theme & Style</th>
                  <th>Impressions & CTR</th>
                  <th className="text-end" style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                      Loading platform events & banners...
                    </td>
                  </tr>
                ) : filteredBanners.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      <RiMegaphoneLine className="font-size-28 text-muted mb-2 opacity-50 d-block mx-auto" />
                      No events or banners found matching the filter.
                      <div className="mt-2">
                        <Button color="primary" size="sm" onClick={handleOpenCreate} className="rounded-pill">
                          + Create First Event / Banner
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredBanners.map((banner) => {
                    const now = new Date();
                    const isExpired = banner.end_date && new Date(banner.end_date) < now;
                    const isScheduled = banner.start_date && new Date(banner.start_date) > now;
                    const isLive = banner.status === 1 && !isExpired && !isScheduled;

                    const styleObj = BANNER_STYLES.find((s) => s.id === banner.banner_style) || BANNER_STYLES[0];

                    return (
                      <tr key={banner.id}>
                        <td>
                          <div className="form-check form-switch form-switch-md mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={banner.status === 1}
                              onChange={() => handleToggleStatus(banner.id, banner.status)}
                              title={banner.status === 1 ? 'Active (Click to disable)' : 'Inactive (Click to activate)'}
                            />
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-column gap-1">
                            <span
                              className="badge text-uppercase font-size-10 px-2 py-0.5 rounded-pill font-weight-bold"
                              style={{
                                backgroundColor:
                                  banner.type === 'event'
                                    ? '#f59e0b'
                                    : banner.type === 'maintenance'
                                    ? '#0284c7'
                                    : banner.type === 'promotional'
                                    ? '#10b981'
                                    : '#7c3aed',
                                color: '#ffffff',
                              }}
                            >
                              {banner.type}
                            </span>
                            <span className="badge bg-light text-dark border font-size-10 px-2 py-0.5 rounded-pill">
                              {banner.badge_text || banner.type.toUpperCase()}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-column">
                            <span className="font-weight-bold text-dark font-size-13 mb-0.5 d-flex align-items-center gap-1">
                              {banner.priority === 'critical' && <span className="text-danger">🔴</span>}
                              {banner.priority === 'urgent' && <span className="text-warning">⚡</span>}
                              {banner.title}
                            </span>
                            <p className="text-muted font-size-12 mb-1 text-truncate" style={{ maxWidth: 340 }}>
                              {banner.message}
                            </p>
                            {banner.action_text && (
                              <div className="d-flex align-items-center gap-1 font-size-11 text-primary">
                                <i className="bx bx-link" />
                                <span>{banner.action_text}</span>
                                {banner.action_url && <span className="text-muted font-size-10">({banner.action_url})</span>}
                              </div>
                            )}
                          </div>
                        </td>

                        <td>
                          {banner.target_scope === 'all' ? (
                            <span className="badge bg-soft bg-success text-success border border-success font-size-11 rounded-pill px-2.5 py-1 d-inline-flex align-items-center gap-1">
                              <RiGlobalLine /> All Clinics (Global)
                            </span>
                          ) : (
                            <div className="d-flex flex-column gap-1">
                              <span className="badge bg-soft bg-info text-info border border-info font-size-11 rounded-pill px-2.5 py-0.5 d-inline-flex align-items-center gap-1">
                                <RiMapPin2Line /> Geo Targeted
                              </span>
                              {banner.target_state && banner.target_state.toLowerCase() !== 'all' && (
                                <small className="text-dark font-weight-semibold font-size-11">
                                  State: <span className="text-primary">{banner.target_state}</span>
                                </small>
                              )}
                              {banner.target_city && banner.target_city.toLowerCase() !== 'all' && (
                                <small className="text-muted font-size-10">
                                  City: {banner.target_city}
                                </small>
                              )}
                              {banner.target_pincode && (
                                <small className="text-muted font-size-10">
                                  Pin: {banner.target_pincode}
                                </small>
                              )}
                            </div>
                          )}
                        </td>

                        <td>
                          <div className="d-flex flex-column font-size-12">
                            {isLive && (
                              <span className="badge bg-soft bg-success text-success font-size-11 rounded-pill mb-1">
                                ● Live Now
                              </span>
                            )}
                            {isScheduled && (
                              <span className="badge bg-soft bg-warning text-warning font-size-11 rounded-pill mb-1">
                                ⏳ Scheduled
                              </span>
                            )}
                            {isExpired && (
                              <span className="badge bg-soft bg-danger text-danger font-size-11 rounded-pill mb-1">
                                ✕ Expired
                              </span>
                            )}
                            <span className="text-dark font-size-11">
                              {banner.start_date ? new Date(banner.start_date).toLocaleDateString() : 'Immediate'}
                            </span>
                            <span className="text-muted font-size-10">
                              to {banner.end_date ? new Date(banner.end_date).toLocaleDateString() : 'Forever'}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div
                            className="rounded px-2 py-1 text-center font-size-11 font-weight-semibold shadow-2xs text-white"
                            style={{ background: styleObj.bg, border: `1px solid ${styleObj.border}` }}
                          >
                            {styleObj.label}
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-column font-size-12">
                            <span className="text-dark font-weight-semibold">
                              {Number(banner.views_count || 0).toLocaleString()} <span className="text-muted font-size-10">views</span>
                            </span>
                            <span className="text-success font-size-11">
                              {Number(banner.clicks_count || 0).toLocaleString()} clicks{' '}
                              <span className="text-muted font-size-10">
                                ({banner.views_count > 0 ? ((banner.clicks_count / banner.views_count) * 100).toFixed(1) + '%' : '0%'})
                              </span>
                            </span>
                          </div>
                        </td>

                        <td className="text-end">
                          <div className="d-flex align-items-center justify-content-end gap-1">
                            <Button
                              color="light"
                              size="sm"
                              className="p-1 px-2 text-primary"
                              onClick={() => handleOpenEdit(banner)}
                              title="Edit Banner"
                            >
                              <RiEditLine />
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              className="p-1 px-2 text-secondary"
                              onClick={() => handleDuplicate(banner)}
                              title="Duplicate"
                            >
                              <RiFileCopyLine />
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              className="p-1 px-2 text-danger"
                              onClick={() => handleDelete(banner.id, banner.title)}
                              title="Delete"
                            >
                              <RiDeleteBinLine />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* CREATE / EDIT MODAL */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg" centered>
        <form onSubmit={handleSave}>
          <ModalHeader toggle={() => setModalOpen(false)} className="bg-light pb-2">
            <div className="d-flex align-items-center gap-2">
              <RiMegaphoneLine className="font-size-20 text-primary" />
              <span className="font-weight-bold">
                {editingId ? `Edit Event / Banner #${editingId}` : 'Create New Event, Announcement & Top Banner'}
              </span>
            </div>
          </ModalHeader>

          <ModalBody className="p-4">
            {/* Live Interactive Preview Box */}
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <span className="text-uppercase font-size-11 font-weight-bold text-muted d-flex align-items-center gap-1">
                  <RiEyeLine /> Real-time Live Top Banner Ribbon Preview
                </span>
                <span className="badge bg-light text-muted border font-size-10">
                  {formData.target_scope === 'all' ? '🌍 Visible to All Tenants' : `📍 Targeted: ${formData.target_state || 'Selected Regions'}`}
                </span>
              </div>

              <div
                className="rounded-3 p-3 text-white shadow-sm position-relative overflow-hidden"
                style={{
                  background: currentStyleObj.bg,
                  border: `1.5px solid ${currentStyleObj.border}`,
                  transition: 'all 0.3s ease',
                }}
              >
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                    {/* Badge */}
                    <span
                      className="badge font-size-11 px-2.5 py-1 rounded-pill font-weight-bold shadow-2xs"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        backdropFilter: 'blur(4px)',
                        border: '1px solid rgba(255, 255, 255, 0.4)',
                        color: '#ffffff',
                      }}
                    >
                      {formData.badge_text || formData.type.toUpperCase()}
                    </span>

                    {/* Content */}
                    <div className="d-flex flex-column">
                      <span className="font-weight-bold font-size-14 line-height-1 mb-0.5">
                        {formData.title || 'Enter your event or announcement headline here...'}
                      </span>
                      <span className="font-size-12 opacity-90 line-height-1.2">
                        {formData.message || 'Write a brief description of the event, workshop, discount, or critical update.'}
                      </span>
                    </div>
                  </div>

                  {/* Actions preview */}
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    {formData.action_text && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light font-size-12 px-3 py-1 font-weight-bold rounded-pill shadow-xs"
                      >
                        {formData.action_text}
                      </button>
                    )}
                    {formData.is_dismissible ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-white p-0 opacity-75 hover-opacity-100"
                        title="Dismiss banner"
                      >
                        <RiCloseLine className="font-size-18" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-3 text-muted opacity-25" />

            {/* Form Fields */}
            <Row>
              <Col md={8}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">
                    Banner Title / Headline <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    required
                    placeholder="e.g. Gujarat Dental Summit 2026 - Registration Open"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Category / Type</Label>
                  <Input
                    type="select"
                    value={formData.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      let badge = newType.toUpperCase();
                      if (newType === 'event') badge = 'UPCOMING EVENT';
                      if (newType === 'maintenance') badge = 'MAINTENANCE';
                      if (newType === 'promotional') badge = 'SPECIAL OFFER';
                      setFormData({ ...formData, type: newType, badge_text: badge });
                    }}
                  >
                    <option value="event">🎪 Event / Workshop</option>
                    <option value="announcement">📢 Announcement</option>
                    <option value="notification">🔔 Notification</option>
                    <option value="promotional">🎁 Promotional / Discount</option>
                    <option value="maintenance">🔧 System Maintenance</option>
                    <option value="info">ℹ️ General Information</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">
                    Announcement Message / Body <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    rows="2"
                    required
                    placeholder="Enter full details, agenda, dates, and instructions for dental clinics..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Badge Ribbon Text</Label>
                  <Input
                    type="text"
                    placeholder="e.g. GUJARAT EVENT"
                    value={formData.badge_text}
                    onChange={(e) => setFormData({ ...formData, badge_text: e.target.value })}
                  />
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Priority Level</Label>
                  <Input
                    type="select"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="critical">🔴 Critical / Emergency</option>
                    <option value="urgent">⚡ Urgent / Important</option>
                    <option value="high">🟡 High</option>
                    <option value="medium">🔵 Medium (Normal)</option>
                    <option value="low">⚪ Low</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Banner Color Theme</Label>
                  <Input
                    type="select"
                    value={formData.banner_style}
                    onChange={(e) => setFormData({ ...formData, banner_style: e.target.value })}
                  >
                    {BANNER_STYLES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              {/* Action Button & Link */}
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Action Button Label (Optional)</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Register Passes, Learn More, Upgrade"
                    value={formData.action_text}
                    onChange={(e) => setFormData({ ...formData, action_text: e.target.value })}
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Action URL / Internal Route</Label>
                  <Input
                    type="text"
                    placeholder="e.g. /subscription, /patients, or https://..."
                    value={formData.action_url}
                    onChange={(e) => setFormData({ ...formData, action_url: e.target.value })}
                  />
                </FormGroup>
              </Col>

              {/* Date Scheduling */}
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">
                    <RiTimerLine className="me-1 text-primary" /> Start Date & Time (Active From)
                  </Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">
                    <RiTimerLine className="me-1 text-danger" /> End Date & Time (Expiry)
                  </Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Target Audience Builder */}
            <div className="p-3 bg-light rounded-3 mb-3 border">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Label className="font-size-13 font-weight-bold text-dark mb-0 d-flex align-items-center gap-1.5">
                  <RiMapPin2Line className="text-primary font-size-16" /> Target Audience & Geographic Filtering
                </Label>
                <div className="d-flex align-items-center gap-3 font-size-12">
                  <div className="form-check mb-0">
                    <input
                      type="radio"
                      id="scope_all"
                      name="target_scope"
                      className="form-check-input"
                      checked={formData.target_scope === 'all'}
                      onChange={() => setFormData({ ...formData, target_scope: 'all', target_state: 'All', target_city: 'All' })}
                    />
                    <label htmlFor="scope_all" className="form-check-label font-weight-semibold">
                      🌍 All Clinics (Global)
                    </label>
                  </div>
                  <div className="form-check mb-0">
                    <input
                      type="radio"
                      id="scope_geo"
                      name="target_scope"
                      className="form-check-input"
                      checked={formData.target_scope === 'geo'}
                      onChange={() => setFormData({ ...formData, target_scope: 'geo' })}
                    />
                    <label htmlFor="scope_geo" className="form-check-label font-weight-semibold text-primary">
                      📍 Geo & Location Targeted
                    </label>
                  </div>
                </div>
              </div>

              {formData.target_scope === 'geo' ? (
                <div className="mt-3">
                  <Row>
                    <Col md={4}>
                      <FormGroup className="mb-2">
                        <Label className="font-size-11 font-weight-semibold text-muted">Target Country</Label>
                        <Input
                          type="text"
                          bsSize="sm"
                          placeholder="e.g. India, All"
                          value={formData.target_country}
                          onChange={(e) => setFormData({ ...formData, target_country: e.target.value })}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup className="mb-2">
                        <Label className="font-size-11 font-weight-semibold text-muted">
                          Target State(s) <span className="text-muted">(comma separated or presets)</span>
                        </Label>
                        <Input
                          type="text"
                          bsSize="sm"
                          placeholder="e.g. Gujarat, Maharashtra"
                          value={formData.target_state}
                          onChange={(e) => setFormData({ ...formData, target_state: e.target.value })}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup className="mb-2">
                        <Label className="font-size-11 font-weight-semibold text-muted">
                          Target City(ies) <span className="text-muted">(optional)</span>
                        </Label>
                        <Input
                          type="text"
                          bsSize="sm"
                          placeholder="e.g. Ahmedabad, Surat, Rajkot"
                          value={formData.target_city}
                          onChange={(e) => setFormData({ ...formData, target_city: e.target.value })}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup className="mb-2">
                        <Label className="font-size-11 font-weight-semibold text-muted">
                          Target Pincode(s) <span className="text-muted">(optional comma separated list)</span>
                        </Label>
                        <Input
                          type="text"
                          bsSize="sm"
                          placeholder="e.g. 380015, 380054, 400001 (leave blank for all pincodes)"
                          value={formData.target_pincode}
                          onChange={(e) => setFormData({ ...formData, target_pincode: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Quick State Selection Chips */}
                  <div className="mt-2">
                    <small className="text-muted font-size-11 d-block mb-1">Quick Select Indian States:</small>
                    <div className="d-flex flex-wrap gap-1">
                      {PRESET_INDIAN_STATES.map((st) => {
                        const currentStates = (formData.target_state || '').split(',').map((s) => s.trim().toLowerCase());
                        const isSelected = currentStates.includes(st.toLowerCase());
                        return (
                          <button
                            key={st}
                            type="button"
                            className={`btn btn-xs rounded-pill font-size-11 ${
                              isSelected ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'
                            }`}
                            onClick={() => handleToggleStatePreset(st)}
                          >
                            {isSelected ? '✓ ' : '+ '}
                            {st}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-dark rounded-pill font-size-11"
                        onClick={() => setFormData({ ...formData, target_state: 'All' })}
                      >
                        Reset to All States
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-2 bg-white rounded text-center text-muted font-size-12 border">
                  <RiGlobalLine className="font-size-16 text-success me-1" />
                  This banner will automatically broadcast to <strong>ALL dental clinics and users</strong> across the platform regardless of location.
                </div>
              )}
            </div>

            {/* Checkboxes */}
            <div className="d-flex align-items-center gap-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="is_dismissible"
                  className="form-check-input"
                  checked={formData.is_dismissible === 1}
                  onChange={(e) => setFormData({ ...formData, is_dismissible: e.target.checked ? 1 : 0 })}
                />
                <label htmlFor="is_dismissible" className="form-check-label font-size-12">
                  Allow tenant to dismiss / close banner (✕)
                </label>
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  id="open_in_new_tab"
                  className="form-check-input"
                  checked={formData.open_in_new_tab === 1}
                  onChange={(e) => setFormData({ ...formData, open_in_new_tab: e.target.checked ? 1 : 0 })}
                />
                <label htmlFor="open_in_new_tab" className="form-check-label font-size-12">
                  Open Action Link in new tab
                </label>
              </div>
            </div>
          </ModalBody>

          <ModalFooter className="bg-light">
            <Button color="light" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting} className="d-inline-flex align-items-center gap-1 font-weight-semibold shadow-2xs">
              {submitting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-1" role="status" />
                  Saving...
                </>
              ) : (
                <>
                  <RiCheckDoubleLine />
                  {editingId ? 'Update Event & Banner' : 'Publish & Broadcast Banner'}
                </>
              )}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* TARGETING SIMULATOR MODAL */}
      <Modal isOpen={simModalOpen} toggle={() => setSimModalOpen(!simModalOpen)} centered>
        <ModalHeader toggle={() => setSimModalOpen(false)} className="bg-light">
          <RiMapPin2Line className="me-1 text-primary" /> Test Targeting Simulator
        </ModalHeader>
        <ModalBody className="p-3">
          <p className="text-muted font-size-12 mb-3">
            Simulate a dental clinic login from different states & cities to test which banners they will see in real-time.
          </p>

          <Row className="mb-3">
            <Col md={6}>
              <Label className="font-size-11 font-weight-semibold">Simulated Clinic State</Label>
              <Input
                type="select"
                bsSize="sm"
                value={simState}
                onChange={(e) => setSimState(e.target.value)}
              >
                {PRESET_INDIAN_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                <option value="Other">Other / International</option>
              </Input>
            </Col>
            <Col md={6}>
              <Label className="font-size-11 font-weight-semibold">Simulated Clinic City</Label>
              <Input
                type="text"
                bsSize="sm"
                value={simCity}
                onChange={(e) => setSimCity(e.target.value)}
                placeholder="e.g. Ahmedabad, Surat"
              />
            </Col>
          </Row>

          <h6 className="font-size-12 font-weight-bold text-dark mb-2">Matching Banners for {simState} / {simCity}:</h6>
          <div className="d-flex flex-column gap-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
            {banners.filter((b) => {
              if (b.status !== 1) return false;
              if (b.target_scope === 'all') return true;
              const states = (b.target_state || '').split(',').map((s) => s.trim().toLowerCase());
              if (!states.includes('all') && !states.includes(simState.toLowerCase()) && !states.some((s) => s.includes(simState.toLowerCase()))) {
                return false;
              }
              return true;
            }).map((b) => (
              <div key={b.id} className="p-2 rounded border bg-light d-flex align-items-center justify-content-between">
                <div>
                  <span className="font-weight-bold font-size-12 text-dark d-block">{b.title}</span>
                  <small className="text-muted font-size-11">
                    {b.target_scope === 'all' ? '🌍 Global Match' : `📍 State: ${b.target_state}`}
                  </small>
                </div>
                <span className="badge bg-success font-size-10">VISIBLE</span>
              </div>
            ))}
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default EventsAndBannersManager;
